import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  CubeIcon,
  ClockIcon,
  BanknotesIcon,
  ChartPieIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { suppliesApi, InventoryTrendAnalytics } from '../api/supplies';
import { useFormatting } from '../hooks/useFormatting';

const numeric = (value: unknown, digits = 2): string => {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toFixed(digits);
};

const SuppliesInventoryTrendAnalytics: React.FC = () => {
  const [trendAnalytics, setTrendAnalytics] = useState<InventoryTrendAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [refreshing, setRefreshing] = useState(false);
  
  const { formatCurrency, formatDate } = useFormatting();

  const fetchTrendAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await suppliesApi.getInventoryTrendAnalytics(dateRange.startDate, dateRange.endDate);
      setTrendAnalytics(data);
    } catch (err) {
      console.error('Error fetching inventory trend analytics:', err);
      setError('Failed to load inventory trend analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTrendAnalytics();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchTrendAnalytics();
  }, [dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-slate-600">Loading inventory trend analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchTrendAnalytics}
          className="bg-cyan-600 text-white px-4 py-2 rounded-md hover:bg-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!trendAnalytics) {
    return (
      <div className="text-center py-8">
        <ChartBarIcon className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500">No trend analytics data available</p>
      </div>
    );
  }

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-cyan-600 bg-cyan-50 border-blue-200';
      default: return 'text-slate-600 bg-slate-50/80 border-gray-200';
    }
  };

  const getRiskLevelColor = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'high': return 'bg-rose-100 text-rose-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-violet-100 text-violet-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ChartBarIcon className="h-6 w-6 text-cyan-600 mr-2" />
            <h2 className="text-xl font-semibold text-slate-900">Inventory Trend Analytics</h2>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-gray-300 rounded-md hover:bg-slate-50/80 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Date Range Controls */}
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate || ''}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate || ''}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setDateRange({})}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-gray-300 rounded-md hover:bg-slate-50/80"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-600">
          <span className="font-medium">Analysis Period:</span> {trendAnalytics.period} | 
          <span className="font-medium ml-2">Generated:</span> {formatDate(new Date(trendAnalytics.generatedAt))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CubeIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Items Analyzed</p>
              <p className="text-2xl font-bold text-slate-900">{trendAnalytics.summary.totalItemsAnalyzed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Low Stock Items</p>
              <p className="text-2xl font-bold text-yellow-600">{trendAnalytics.summary.lowStockItems}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingUpIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Overstocked Items</p>
              <p className="text-2xl font-bold text-cyan-600">{trendAnalytics.summary.overstockedItems}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Expired Items</p>
              <p className="text-2xl font-bold text-red-600">{trendAnalytics.summary.expiredItems}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Total Value</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(trendAnalytics.summary.totalInventoryValue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Inventory Efficiency</p>
            <p className="text-2xl font-bold text-cyan-600">{trendAnalytics.performanceMetrics.inventoryEfficiency}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Cost Efficiency</p>
            <p className="text-2xl font-bold text-green-600">{trendAnalytics.performanceMetrics.costEfficiency}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Stock Accuracy</p>
            <p className="text-2xl font-bold text-purple-600">{trendAnalytics.performanceMetrics.stockAccuracy}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Total Value</p>
            <p className="text-2xl font-bold text-slate-900">
              {formatCurrency(trendAnalytics.performanceMetrics.totalInventoryValue)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Avg Turnover</p>
            <p className="text-2xl font-bold text-cyan-600">
              {numeric(trendAnalytics.performanceMetrics.averageTurnover)}x
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Optimization Score</p>
            <p className="text-2xl font-bold text-emerald-600">{trendAnalytics.performanceMetrics.optimizationScore}%</p>
          </div>
        </div>
      </div>

      {/* Monthly Inventory Trends Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Monthly Inventory Trends</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Month</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Low Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Expired</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trendAnalytics.inventoryTrends.monthlyData.map((month) => (
                <tr key={month.month}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {new Date(month.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{month.totalItems}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency(month.totalValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{month.totalQuantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency(month.averageValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{month.lowStockItems}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{month.expiredItems}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Growth Trends */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-cyan-50 p-4 rounded-lg">
            <div className="flex items-center">
              <ArrowTrendingUpIcon className="h-5 w-5 text-cyan-600 mr-2" />
              <span className="text-sm font-medium text-cyan-700">Inventory Growth</span>
            </div>
            <p className="text-lg font-bold text-blue-900 mt-1">
              {numeric(trendAnalytics.inventoryTrends?.trends?.inventoryGrowth, 1)}%
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-700">Quantity Growth</span>
            </div>
            <p className="text-lg font-bold text-green-900 mt-1">
              {numeric(trendAnalytics.inventoryTrends?.trends?.quantityGrowth, 1)}%
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center">
              <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-purple-700">Item Growth</span>
            </div>
            <p className="text-lg font-bold text-purple-900 mt-1">
              {numeric(trendAnalytics.inventoryTrends?.trends?.itemGrowth, 1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Alerts and Recommendations */}
      {trendAnalytics.alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-yellow-600" />
            Alerts & Recommendations
          </h3>
          <div className="space-y-3">
            {trendAnalytics.alerts.slice(0, 10).map((alert, index) => (
              <div key={index} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                <div className="flex items-start">
                  <div className="flex-1">
                    <p className="font-medium">{alert.message}</p>
                    <p className="text-sm mt-1 opacity-75">{alert.recommendation}</p>
                    {alert.itemName && (
                      <p className="text-xs mt-1 opacity-60">Item: {alert.itemName}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stock Movement Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Top Stock Movements</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Current Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Reorder Point</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Days to Reorder</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Turnover</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trendAnalytics.stockMovements.slice(0, 10).map((movement) => (
                <tr key={movement.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{movement.name}</div>
                      <div className="text-sm text-slate-500">{movement.type}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{movement.currentStock}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{movement.reorderPoint}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      movement.stockStatus === 'IN_STOCK' ? 'bg-emerald-100 text-emerald-800' :
                      movement.stockStatus === 'LOW_STOCK' ? 'bg-amber-100 text-amber-800' :
                      movement.stockStatus === 'OUT_OF_STOCK' ? 'bg-rose-100 text-rose-800' :
                      'bg-violet-100 text-violet-800'
                    }`}>
                      {movement.stockStatus.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {movement.daysUntilReorder === 999 ? 'N/A' : `${movement.daysUntilReorder} days`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {numeric(movement.stockTurnover)}x
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seasonal Patterns */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2" />
          Seasonal Patterns
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Seasonal Distribution</h4>
            <div className="space-y-2">
              {Object.entries(trendAnalytics.seasonalPatterns.seasonal).map(([season, value]) => (
                <div key={season} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 capitalize">{season}</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(value)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-sm text-slate-600">
              Peak Season: <span className="font-medium text-slate-900 capitalize">{trendAnalytics.seasonalPatterns.peakSeason}</span>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Monthly Distribution</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {trendAnalytics.seasonalPatterns.monthly.map((month) => (
                <div key={month.month} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{month.monthName}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-slate-900">{formatCurrency(month.usage)}</span>
                    <span className="text-slate-500">({numeric(month.percentage, 1)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Forecasting */}
      {trendAnalytics.forecasting.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            Inventory Forecasting
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Next Restock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Order Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">30-Day Forecast</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trendAnalytics.forecasting.slice(0, 10).map((forecast) => (
                  <tr key={forecast.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{forecast.name}</div>
                        <div className="text-sm text-slate-500">{forecast.type}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDate(new Date(forecast.nextRestockDate))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {forecast.optimumOrderQuantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {numeric(forecast.forecasts[0]?.projectedQuantity, 1)} units
                      </div>
                      <div className="text-xs text-slate-500">
                        {Math.round((forecast.forecasts[0]?.confidence || 0) * 100)}% confidence
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Turnover Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Inventory Turnover Analysis</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Inventory Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Turnover Ratio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Days on Hand</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Efficiency Score</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trendAnalytics.turnoverAnalysis.slice(0, 10).map((turnover) => (
                <tr key={turnover.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{turnover.name}</div>
                      <div className="text-sm text-slate-500">{turnover.type}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency(turnover.inventoryValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {numeric(turnover.turnoverRatio)}x
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {numeric(turnover.daysOnHand, 0)} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm text-slate-900 mr-2">{numeric(turnover.efficiency, 0)}%</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-cyan-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(100, turnover.efficiency)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuppliesInventoryTrendAnalytics; 